import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import AdminNav, { AdminHeader } from "@/components/AdminNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Pencil, Upload } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["not_connected", "test", "live", "disabled"];

export default function AdminSuppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tdFile, setTdFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importSummary, setImportSummary] = useState(null);

  const load = () => api.get("/admin/suppliers").then(({ data }) => setSuppliers(data.suppliers)).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const doTdImport = async () => {
    if (!tdFile || importing) return;
    setImporting(true);
    setImportSummary(null);
    const fd = new FormData();
    fd.append("file", tdFile);
    try {
      const { data } = await api.post("/admin/suppliers/td_synnex/import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportSummary(data);
      toast.success(`TD SYNNEX import — ${data.imported} imported, ${data.updated} updated`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="container-tight py-8" data-testid="admin-suppliers-page">
      <AdminNav />
      <div className="flex items-start justify-between">
        <AdminHeader title="SUPPLIERS" subtitle="Authorized distributors / fulfillment partners. Credentials never stored in DB — env vars only." />
        <SupplierDialog onSaved={load}>
          <Button data-testid="new-supplier-btn" className="bg-[#FF5C00] hover:bg-[#FF7A33] rounded-sm font-mono tracking-wider">
            <Plus className="w-4 h-4 mr-2" />NEW SUPPLIER
          </Button>
        </SupplierDialog>
      </div>

      <div className="border border-zinc-800 bg-[#0a0a0c] rounded-sm p-4 mb-6"
           data-testid="td-synnex-import-panel">
        <div className="font-mono text-[10px] tracking-widest text-zinc-500 uppercase mb-2">
          TD SYNNEX — LAUNCH CATALOG IMPORT
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <input type="file" accept=".csv"
                 data-testid="td-csv-input"
                 onChange={(e) => setTdFile(e.target.files?.[0] || null)}
                 className="text-xs font-mono text-zinc-300 file:mr-3 file:py-1.5 file:px-3 file:border-0 file:bg-zinc-800 file:text-zinc-200 file:rounded-sm hover:file:bg-zinc-700" />
          <Button data-testid="td-import-btn"
                  disabled={!tdFile || importing}
                  onClick={doTdImport}
                  className="bg-[#FF5C00] hover:bg-[#FF7A33] rounded-sm font-mono text-xs tracking-widest uppercase disabled:opacity-50">
            <Upload className="w-3.5 h-3.5 mr-2" />
            {importing ? "Importing…" : "Import CSV"}
          </Button>
          {tdFile && !importing && (
            <span className="font-mono text-[11px] text-zinc-500">{tdFile.name}</span>
          )}
        </div>
        <p className="text-[11px] text-zinc-500 mt-2">
          Only rows with PublishStatus=APPROVED are imported. Idempotent — re-uploading
          the same CSV updates existing rows. TD SYNNEX provider remains not_connected;
          no live orders are placed.
        </p>
        {importSummary && (
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs font-mono"
               data-testid="td-import-summary">
            <SumStat label="Rows" v={importSummary.rows_seen} />
            <SumStat label="Imported" v={importSummary.imported} color="#22c55e" />
            <SumStat label="Updated" v={importSummary.updated} color="#eab308" />
            <SumStat label="Skipped HOLD" v={importSummary.skipped_hold} />
            <SumStat label="Unmapped Cat." v={importSummary.skipped_unmapped_category} />
            <SumStat label="Below MAP" v={importSummary.skipped_below_map} />
            <SumStat label="Invalid" v={importSummary.invalid} color="#ef4444" />
            <SumStat label="Low Stock" v={importSummary.low_stock_warnings} color="#eab308" />
          </div>
        )}
      </div>

      {loading ? <div className="font-mono text-zinc-500">LOADING...</div> : (
        <div className="border border-zinc-800 bg-[#0a0a0c] rounded-sm overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="font-mono text-[10px] tracking-widest text-zinc-500 uppercase border-b border-zinc-900 bg-black/40">
                <th className="p-3 text-left">Code</th>
                <th className="p-3 text-left">Name</th>
                <th className="p-3 text-left">Status</th>
                <th className="p-3 text-right">Priority</th>
                <th className="p-3 text-left">Drop-Ship</th>
                <th className="p-3 text-left">API Ref</th>
                <th className="p-3 text-left">Provider</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {suppliers.map((s) => (
                <tr key={s.id} className="border-b border-zinc-900 hover:bg-black/40" data-testid={`supplier-${s.code}`}>
                  <td className="p-3 font-mono text-xs">{s.code}</td>
                  <td className="p-3 text-white">{s.name}</td>
                  <td className="p-3"><StatusBadge status={s.status} /></td>
                  <td className="p-3 font-mono text-right">{s.priority}</td>
                  <td className="p-3 text-xs">{s.drop_ship_capable ? "Yes" : "No"}</td>
                  <td className="p-3 font-mono text-[10px] text-zinc-500">{s.auth_config_ref || "—"}</td>
                  <td className="p-3 text-xs">{s.provider_registered ? <span className="text-green-400">registered</span> : <span className="text-red-400">missing</span>}</td>
                  <td className="p-3">
                    <SupplierDialog supplier={s} onSaved={load}>
                      <button data-testid={`edit-${s.code}`} className="text-zinc-400 hover:text-[#FF5C00]"><Pencil className="w-4 h-4" /></button>
                    </SupplierDialog>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-4 border border-zinc-800 bg-[#0a0a0c] p-4 rounded-sm text-xs text-zinc-400 leading-relaxed">
        <div className="font-mono text-[10px] tracking-widest text-[#00F0FF] uppercase mb-2">// notes</div>
        Statuses of <span className="font-mono text-yellow-400">not_connected</span> suppliers will raise <code>SupplierNotConnectedError</code> at fulfillment time and route the order to the <b>Human Review</b> queue. Set to <span className="font-mono text-blue-400">test</span> or <span className="font-mono text-green-400">live</span> only after the real provider API client has been wired and credentials injected via env vars.
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const style = {
    live: "bg-green-500/10 text-green-400 border-green-500/30",
    test: "bg-blue-500/10 text-blue-400 border-blue-500/30",
    not_connected: "bg-yellow-500/10 text-yellow-400 border-yellow-500/30",
    disabled: "bg-red-500/10 text-red-400 border-red-500/30",
  }[status] || "bg-zinc-500/10 text-zinc-400 border-zinc-500/30";
  return <span className={`font-mono text-[10px] tracking-widest uppercase px-2 py-0.5 border rounded-sm ${style}`}>{status}</span>;
}

function SumStat({ label, v, color = "#e5e5e5" }) {
  return (
    <div className="border border-zinc-900 bg-black/40 rounded-sm px-2 py-1.5">
      <div className="text-[9px] tracking-widest text-zinc-500 uppercase">{label}</div>
      <div className="font-bold" style={{ color }}>{v ?? 0}</div>
    </div>
  );
}

function SupplierDialog({ supplier, onSaved, children }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(supplier || {
    code: "", name: "", status: "not_connected", priority: 50,
    drop_ship_capable: true, return_days: 30, api_endpoint_ref: "", auth_config_ref: "", notes: "",
  });
  const isEdit = !!supplier;

  const save = async () => {
    try {
      if (isEdit) await api.patch(`/admin/suppliers/${supplier.id}`, form);
      else await api.post("/admin/suppliers", form);
      toast.success(isEdit ? "Supplier updated" : "Supplier created");
      setOpen(false); onSaved && onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="bg-[#0a0a0c] border-zinc-800 max-w-lg rounded-sm">
        <DialogHeader><DialogTitle className="font-display font-black">{isEdit ? `EDIT ${supplier.code.toUpperCase()}` : "NEW SUPPLIER"}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-2">
          {!isEdit && <Field label="Code (unique)" value={form.code} onChange={(v) => setForm({...form, code: v})} testId="sup-code" />}
          <Field label="Display Name" value={form.name} onChange={(v) => setForm({...form, name: v})} testId="sup-name" />
          <div>
            <Label className="font-mono text-xs tracking-widest text-zinc-400 uppercase">Status</Label>
            <Select value={form.status} onValueChange={(v) => setForm({...form, status: v})}>
              <SelectTrigger data-testid="sup-status" className="bg-[#050505] border-zinc-800 rounded-sm mt-1"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#0a0a0c] border-zinc-800">
                {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Field label="Priority (0-100)" type="number" value={form.priority} onChange={(v) => setForm({...form, priority: Number(v)})} testId="sup-priority" />
          <Field label="API endpoint reference (e.g., env:DNH_API_URL)" value={form.api_endpoint_ref || ""} onChange={(v) => setForm({...form, api_endpoint_ref: v})} testId="sup-api-endpoint" />
          <Field label="Auth config reference (e.g., env:DNH_API_KEY)" value={form.auth_config_ref || ""} onChange={(v) => setForm({...form, auth_config_ref: v})} testId="sup-auth-ref" />
          <Field label="Notes" value={form.notes || ""} onChange={(v) => setForm({...form, notes: v})} testId="sup-notes" />
          <Button data-testid="sup-save" onClick={save} className="w-full bg-[#FF5C00] hover:bg-[#FF7A33] rounded-sm font-mono tracking-wider">
            {isEdit ? "UPDATE SUPPLIER" : "CREATE SUPPLIER"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, value, onChange, type = "text", testId }) {
  return (
    <div>
      <Label className="font-mono text-xs tracking-widest text-zinc-400 uppercase">{label}</Label>
      <Input data-testid={testId} type={type} value={value} onChange={(e) => onChange(e.target.value)}
        className="bg-[#050505] border-zinc-800 rounded-sm mt-1 focus-visible:ring-[#FF5C00]" />
    </div>
  );
}
